"use client";

import { motion } from "framer-motion";

import { cn } from "@/lib/utils";

import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { ProjectStatus, ProjectStatusValues } from "@paladin-monorepo/common";

const Timeline = ({ status }: { status: ProjectStatus }) => {
  const { t } = useTranslation();
  const currentPhase = ProjectStatusValues.indexOf(status);

  const timelinePhases = [
    {
      id: 0,
      date: "",
      title: t('workspace.status.Contracting'),
      description:
        t('workspace.phaseStatusDescription.Contracting'),
    },
    {
      id: 1,
      date: "",
      title: t('workspace.status.Kickoff'),
      description:
        t('workspace.phaseStatusDescription.Kickoff'),
    },
    {
      id: 2,
      date: "",
      title: t('workspace.status.InProgress'),
      description:
        t('workspace.phaseStatusDescription.InProgress'),
    },
    {
      id: 3,
      date: "",
      title: t('workspace.status.Closure'),
      description:
        t('workspace.phaseStatusDescription.Closure'),
    },
    {
      id: 4,
      date: "",
      title: t('workspace.status.Completed'),
      description:
        t('workspace.phaseStatusDescription.Completed'),
    },
  ];

  return (
    <section className="bg-background">
      <div className="container flex flex-col items-center">
        <Card className="relative w-full border-none shadow-none md:py-16">
          <CardContent className="p-0">
            <div className="relative flex flex-col items-center md:mt-12">
              <Separator className="absolute -top-8 left-0 hidden md:block" />
              {currentPhase > -1 && (
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{
                    width: currentPhase >= timelinePhases.length - 1 ? '100%' : `${(currentPhase / timelinePhases.length) * 104}%`,
                  }}
                  transition={{ ease: "easeOut", duration: 0.5 }}
                  className={cn(
                    "bg-foreground absolute -top-[32px] left-0 hidden h-0.5 md:block",
                  )}
                />
              )}

              <div className="grid gap-6 md:grid-cols-5">
                {timelinePhases.map((phase, index) => (
                  <div key={phase.id} className="relative space-y-2">
                    <Separator
                      orientation="vertical"
                      className="absolute left-0 top-6 block md:hidden"
                    />
                    {index === 0 && (
                      <motion.div
                        initial={{ height: 0 }}
                        whileInView={{
                          height: currentPhase * 112,
                        }}
                        transition={{ ease: "easeOut", duration: 0.5 }}
                        className={cn(
                          "bg-foreground absolute left-0 z-10 w-0.5 md:hidden",
                        )}
                      />
                    )}
                    <div className="bg-foreground absolute -left-[9px] top-0 z-10 mb-5 flex size-5 items-center justify-center rounded-full p-1 md:-top-10 md:left-0">
                      <div className={cn("size-full rounded-full", index <= currentPhase ? "bg-foreground" : "bg-background")} />
                    </div>

                    <div className="pl-7 md:pl-0">
                      <p className="text-muted-foreground text-sm">
                        {phase.date}
                      </p>
                      <h2 className="text-foreground text-xl font-bold tracking-tighter">
                        {phase.title}
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export { Timeline };
